import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert
} from '@mui/material';
import { useForm } from 'react-hook-form';

export const LisTestItemMasterHelpForm = ({ 
  onClose, 
  onAdd, 
  parameters = [], 
  existingHelpDescriptions = '' // Now expecting string instead of array
}) => {
  const [selectedParameter, setSelectedParameter] = useState('');
  const [helpText, setHelpText] = useState('');

  const { handleSubmit, reset } = useForm();

  // Initialize with existing help description when parameter is selected
  useEffect(() => {
    if (selectedParameter) {
      const param = parameters.find(p => p._id === selectedParameter || p.tempId === selectedParameter);
      setHelpText(param?.helpDescriptions || '');
    } else {
      setHelpText('');
    }
  }, [selectedParameter, parameters]);

  const handleSaveHelpDescription = () => {
    if (!selectedParameter) {
      alert('Please select a parameter first');
      return;
    }

    if (!helpText.trim()) {
      alert('Please enter help description');
      return;
    }

    const helpData = {
      parameterId: selectedParameter,
      helpText: helpText.trim()
    };

    onAdd(helpData);
    onClose();
  };

  const getParameterName = (parameterId) => {
    const param = parameters.find(p => p._id === parameterId || p.tempId === parameterId);
    return param ? param.name : 'Unknown Parameter';
  };

  const hasExistingHelp = parameters.find(p => 
    (p._id === selectedParameter || p.tempId === selectedParameter)
  )?.helpDescriptions;

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5" fontWeight="bold">
          Help Description Management
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Add or edit help description for parameters
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Parameter Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Select Parameter</InputLabel>
              <Select
                value={selectedParameter}
                onChange={(e) => setSelectedParameter(e.target.value)}
                label="Select Parameter"
              >
                {parameters.map((param) => (
                  <MenuItem key={param._id || param.tempId} value={param._id || param.tempId}>
                    {param.name} {param.unit ? `(${param.unit})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {selectedParameter && (
            <>
              {/* Help Description Form */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Help Description for {getParameterName(selectedParameter)}
                </Typography>
                
                {hasExistingHelp && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This parameter already has a help description. Editing will replace the existing one.
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit(handleSaveHelpDescription)}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Help Description
                      </Typography>
                      <TextField
                        multiline
                        rows={6}
                        fullWidth
                        variant="outlined"
                        value={helpText}
                        onChange={(e) => setHelpText(e.target.value)}
                        placeholder="Enter help description for this parameter..."
                        inputProps={{
                          maxLength: 1000
                        }}
                        label="Help Description"
                      />
                      <Typography variant="caption" color="textSecondary">
                        {helpText.length}/1000 characters
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Existing Help Description Preview */}
              {hasExistingHelp && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Current Help Description
                  </Typography>
                  <Box 
                    sx={{ 
                      p: 2,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      borderRadius: 1,
                      backgroundColor: 'grey.50',
                      maxHeight: '150px',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {hasExistingHelp}
                  </Box>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        {/* <Button onClick={handleCancel}>Cancel</Button> */}
        <Button 
          onClick={handleSaveHelpDescription} 
          variant="contained" 
          disabled={!selectedParameter || !helpText.trim()}
        >
          {hasExistingHelp ? 'Update Help Description' : 'Save Help Description'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};